import { useState, useEffect } from 'react';
import { ResponsiveContainer, Treemap, Tooltip } from 'recharts';
import api from '../api/api'; // Используем единый API инстанс
import { RefreshCw, Activity } from 'lucide-react';

// Кастомный блок внутри квадрата
const CustomizedContent = (props: any) => {
  const { x, y, width, height, name, change } = props;

  // Цвета в зависимости от процента изменения
  let bg = '#1e293b'; // Серый (0%)
  const val = parseFloat(change);

  if (val > 5) bg = '#14532d';      // Темно-зеленый (PUMP)
  else if (val > 0) bg = '#15803d'; // Зеленый
  else if (val < -5) bg = '#7f1d1d';// Темно-красный (DUMP)
  else if (val < 0) bg = '#b91c1c'; // Красный

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: bg,
          stroke: '#0f172a',
          strokeWidth: 2,
          strokeOpacity: 1,
        }}
      />
      {width > 35 && height > 35 && (
        <text
          x={x + width / 2}
          y={y + height / 2 - 6}
          textAnchor="middle"
          fill="#fff"
          fontSize={Math.min(width / 4, 14)}
          fontWeight="bold"
        >
          {name}
        </text>
      )}
      {width > 35 && height > 35 && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 8}
          textAnchor="middle"
          fill="rgba(255,255,255,0.7)"
          fontSize={Math.min(width / 5, 10)}
        >
          {val > 0 ? '+' : ''}{val.toFixed(1)}%
        </text>
      )}
    </g>
  );
};

// Кастомный тултип при наведении
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-slate-900 border border-slate-700 p-2 rounded shadow-xl text-xs z-50">
        <p className="font-bold text-white">{data.name}</p>
        <p className={data.change >= 0 ? 'text-green-400' : 'text-red-400'}>
          Изменение: {data.change > 0 ? '+' : ''}{parseFloat(data.change).toFixed(2)}%
        </p>
        <p className="text-slate-400">Объем: ${(data.value / 1000000).toFixed(1)}M</p>
      </div>
    );
  }
  return null;
};

export const MarketHeatmap = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await api.get('/scanner/heatmap');

      // Фильтруем и сортируем для красоты
      const validData = res.data
        .filter((i: any) => i.value > 0) // Убираем монеты без объема
        .sort((a: any, b: any) => b.value - a.value); // Сортировка: Самые большие слева

      setData(validData);
      setLoading(false);
    } catch (e) {
      console.error("Heatmap error", e);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Обновляем каждые 30 сек
    return () => clearInterval(interval);
  }, []);

  if (loading && data.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 bg-[#1e293b]/30 animate-pulse">
        <Activity size={32} className="mb-2 text-blue-500" />
        <span className="text-xs font-bold uppercase tracking-widest">Анализ рынка...</span>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative group">
      {/* Кнопка обновления (появляется при наведении) */}
      <button
        onClick={() => { setLoading(true); fetchData(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-black/40 hover:bg-black/70 rounded-lg text-slate-400 hover:text-white transition-all opacity-0 group-hover:opacity-100 backdrop-blur-sm"
        title="Обновить карту"
      >
        <RefreshCw size={14} />
      </button>

      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={data}
            dataKey="value"
            aspectRatio={4 / 3}
            stroke="#0f172a"
            content={<CustomizedContent />}
            isAnimationActive={false} // Выключаем анимацию для быстродействия
          >
            <Tooltip content={<CustomTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
          Нет данных о рынке.
        </div>
      )}
    </div>
  );
};